import { useCallback, useEffect, useRef } from "react";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PERMISSIONS } from "../../constants";
import { useAuth } from "../auth/identity-auth0-context";
import { useNotification } from "../../context/notification-provider";
import ROUTES from "../../routes/constants";
import { getLongRunningTasks } from "./task-info-service";
import {
  clearLastNotifiedSignature,
  getLastNotifiedSignature,
  getLongRunningTasksSignature,
  LONG_RUNNING_TASKS_CHECK_DOWN_SIGNATURE,
  setLastNotifiedSignature,
} from "./long-running-tasks-notification-state";

export const LONG_RUNNING_TASKS_POLL_INTERVAL = 60 * 60 * 1000;

const getNotificationMessage = (count: number) => {
  return count === 1
    ? "There is 1 long running task that needs your attention."
    : `There are ${count} long running tasks that need your attention.`;
};

const LongRunningTasksNotificationAction = ({
  onClick,
}: {
  onClick: () => void;
}) => (
  <Button color="inherit" size="small" onClick={onClick}>
    View tasks
  </Button>
);

const LongRunningTasksBackgroundNotifier = () => {
  const { hasAccess } = useAuth();
  const { hideNotification, showNotification } = useNotification();
  const navigate = useNavigate();
  const canViewAdmin = hasAccess(PERMISSIONS.VIEW_ADMIN);
  const hideNotificationRef = useRef(hideNotification);
  const navigateRef = useRef(navigate);
  const showNotificationRef = useRef(showNotification);
  const currentNotificationSignatureRef = useRef<string | null>(null);

  hideNotificationRef.current = hideNotification;
  navigateRef.current = navigate;
  showNotificationRef.current = showNotification;

  const handleViewLongRunningTasks = useCallback(() => {
    if (currentNotificationSignatureRef.current) {
      setLastNotifiedSignature(currentNotificationSignatureRef.current);
    }
    hideNotificationRef.current();
    navigateRef.current(`${ROUTES.TASKS}?tab=long-running`);
  }, []);

  const checkLongRunningTasks = useCallback(async () => {
    try {
      const response = await getLongRunningTasks();
      const longRunningTaskCount = response.results?.length ?? 0;

      if (longRunningTaskCount === 0) {
        clearLastNotifiedSignature();
        return;
      }

      const currentSignature = getLongRunningTasksSignature(response.results);

      if (currentSignature === getLastNotifiedSignature()) {
        return;
      }

      currentNotificationSignatureRef.current = currentSignature;
      setLastNotifiedSignature(currentSignature);

      showNotificationRef.current({
        message: getNotificationMessage(longRunningTaskCount),
        severity: "warning",
        duration: null,
        action: (
          <LongRunningTasksNotificationAction
            onClick={handleViewLongRunningTasks}
          />
        ),
      });
    } catch {
      currentNotificationSignatureRef.current =
        LONG_RUNNING_TASKS_CHECK_DOWN_SIGNATURE;
      if (
        currentNotificationSignatureRef.current === getLastNotifiedSignature()
      ) {
        return;
      }
      setLastNotifiedSignature(currentNotificationSignatureRef.current);

      showNotificationRef.current({
        message: "Long running tasks check is down.",
        severity: "error",
        duration: null,
        action: (
          <LongRunningTasksNotificationAction
            onClick={handleViewLongRunningTasks}
          />
        ),
      });
    }
  }, [handleViewLongRunningTasks]);

  useEffect(() => {
    if (!canViewAdmin) return undefined;

    checkLongRunningTasks();

    const interval = window.setInterval(
      checkLongRunningTasks,
      LONG_RUNNING_TASKS_POLL_INTERVAL,
    );

    return () => window.clearInterval(interval);
  }, [canViewAdmin, checkLongRunningTasks]);

  return null;
};

export default LongRunningTasksBackgroundNotifier;
